import React, { useState, useMemo, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight } from "lucide-react";
import EngineIcon from "./EngineIcon";
import { speak, cancelSpeech } from "@/lib/speak";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Hoisted so it isn't redefined each render (prevents remount/dropped taps).
function MatchTile({ item, active, isMatched, isWrong, onClick, testid }) {
  return (
    <motion.button
      animate={isWrong ? { x: [0, -6, 6, -4, 0] } : {}}
      transition={{ duration: 0.4 }}
      onClick={onClick}
      disabled={isMatched}
      data-testid={testid}
      className={`flex w-full items-center gap-3 rounded-2xl border-2 px-4 py-3 text-left font-heading font-bold transition-colors ${
        isMatched ? "border-[#10B981] bg-[#10B981]/10 text-[#047857]"
          : isWrong ? "border-[#E05A47] bg-[#E05A47]/10 text-[#E05A47]"
          : active ? "border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488]"
          : "border-[#E5DECF] bg-white text-[#1F2937] hover:border-[#0D9488]"
      }`}>
      {item.icon && <span className="shrink-0"><EngineIcon name={item.icon} size={22} /></span>}
      <span className="flex-1">{item.label}</span>
      {isMatched && <CheckCircle2 size={18} />}
    </motion.button>
  );
}

// Reusable matching engine (tap a word, then tap its match). Works great on touch.
export default function MatchEngine({ activity, onComplete, autoplay = false }) {
  const cfg = activity.config || {};
  const pairs = cfg.pairs || [];
  const rights = useMemo(() => shuffle(pairs.map((p, i) => ({ ...p, i }))), [pairs]);

  const [selected, setSelected] = useState(null); // left index
  const [matched, setMatched] = useState([]); // matched pair indexes
  const [wrong, setWrong] = useState(null); // right index flashing wrong
  const [wrongTries, setWrongTries] = useState(0);
  const advanced = useRef(false);

  const allMatched = matched.length === pairs.length && pairs.length > 0;

  useEffect(() => {
    if (autoplay && activity.prompt) speak(activity.prompt, "nova", { speed: 0.9 });
    return () => cancelSpeech();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activity.id, autoplay]);

  useEffect(() => {
    if (autoplay && allMatched && !advanced.current) {
      advanced.current = true;
      speak("All matched! Great job!", "nova", { speed: 0.95 });
      const t = setTimeout(() => onComplete(wrongTries === 0 ? 100 : 85), 1600);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMatched]);

  const pickLeft = (i) => {
    if (matched.includes(i)) return;
    setSelected(i);
    if (cfg.speak) speak(pairs[i].left);
  };

  const pickRight = (rightIdx) => {
    if (selected === null || matched.includes(rightIdx)) return;
    if (rightIdx === selected) {
      setMatched((m) => [...m, rightIdx]);
      setSelected(null);
    } else {
      setWrongTries((w) => w + 1);
      setWrong(rightIdx);
      setTimeout(() => setWrong(null), 500);
    }
  };

  return (
    <div data-testid="match-engine">
      <p className="text-center font-body text-base sm:text-lg text-[#6B7280]">{activity.prompt}</p>

      <div className="mx-auto mt-6 grid max-w-2xl grid-cols-2 gap-5">
        <div>
          <div className="mb-2 text-center font-body text-xs font-bold uppercase tracking-wide text-[#94A3B8]">{cfg.leftLabel || "Item"}</div>
          <div className="space-y-3">
            {pairs.map((p, i) => (
              <MatchTile key={i} testid={`match-left-${i}`}
                item={{ label: p.left, icon: p.leftIcon }}
                active={selected === i} isMatched={matched.includes(i)} isWrong={false}
                onClick={() => pickLeft(i)} />
            ))}
          </div>
        </div>
        <div>
          <div className="mb-2 text-center font-body text-xs font-bold uppercase tracking-wide text-[#94A3B8]">{cfg.rightLabel || "Match"}</div>
          <div className="space-y-3">
            {rights.map((p) => (
              <MatchTile key={p.i} testid={`match-right-${p.i}`}
                item={{ label: p.right, icon: p.rightIcon }}
                active={false} isMatched={matched.includes(p.i)} isWrong={wrong === p.i}
                onClick={() => pickRight(p.i)} />
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8 text-center">
        {allMatched ? (
          <button onClick={() => onComplete(wrongTries === 0 ? 100 : 85)} data-testid="match-continue"
            className="inline-flex items-center gap-2 rounded-full bg-[#10B981] px-8 py-3 font-heading font-bold text-white shadow-md transition-transform hover:scale-105 active:scale-95">
            <CheckCircle2 size={18} /> All matched! Next <ArrowRight size={18} />
          </button>
        ) : (
          <p className="font-body text-sm text-[#94A3B8]">
            {selected === null ? `Tap a ${cfg.leftLabel?.toLowerCase() || "item"} on the left.` : "Now tap its match on the right."}
          </p>
        )}
      </div>
    </div>
  );
}
